import React, { useMemo, useState } from "react";
import { Tab, Tabs } from "react-bootstrap";

const PAGE_SIZE = 10;

const getFileName = (file) => file.fileName || file.savedFileName ||
  String(file.storagePath || "").split("/").pop() || "Untitled file";

const getUploadDate = (value) => {
  if (!value) return null;
  const date = typeof value.toDate === "function" ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatUploadDate = (value) => {
  const date = getUploadDate(value);
  return date ? date.toLocaleString("en-GB", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", timeZone: "Asia/Bangkok",
  }) : "-";
};

const FollowupFileList = ({ title, files, onViewFile, isPreviewLoading }) => {
  const [page, setPage] = useState(1);
  const sortedFiles = useMemo(() => [...files].sort((left, right) => {
    const leftDate = getUploadDate(left.uploadedAt);
    const rightDate = getUploadDate(right.uploadedAt);
    // Keep undated files accessible after the newest uploads.
    if (!leftDate || !rightDate) return leftDate ? -1 : rightDate ? 1 : 0;
    return rightDate - leftDate;
  }), [files]);
  const pageCount = Math.max(1, Math.ceil(sortedFiles.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const start = (currentPage - 1) * PAGE_SIZE;
  const visibleFiles = sortedFiles.slice(start, start + PAGE_SIZE);

  return (
    <div className="pt-3">
      {visibleFiles.length ? (
        <div className="table-responsive">
          <table className="table table-sm table-striped mb-2" style={{ minWidth: "520px", tableLayout: "fixed" }}>
            <thead>
              <tr>
                <th scope="col" style={{ width: "50%" }}>File</th>
                <th scope="col" style={{ width: "25%" }}>Uploaded</th>
                <th scope="col" style={{ width: "25%" }}>Uploaded by</th>
              </tr>
            </thead>
            <tbody>
              {visibleFiles.map((file, index) => (
                <tr key={`${file.id || file.storagePath || getFileName(file)}-${start + index}`}>
                  <td style={{ overflowWrap: "break-word" }}>
                    <button type="button" className="btn btn-link p-0 text-left"
                      style={{ whiteSpace: "normal", overflowWrap: "anywhere", maxWidth: "100%" }}
                      disabled={isPreviewLoading} onClick={() => onViewFile(file)}>
                      <i className="fa fa-file-o mr-2" aria-hidden="true" />
                      {getFileName(file)}
                    </button>
                  </td>
                  <td>{formatUploadDate(file.uploadedAt)}</td>
                  <td style={{ overflowWrap: "anywhere" }}>{file.uploadedBy || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-muted py-3 mb-2">
          No files
        </p>
      )}
      <div className="d-flex flex-wrap justify-content-between align-items-center">
        <small className="text-muted mb-2" role="status">
          {sortedFiles.length ? `${start + 1}–${Math.min(start + PAGE_SIZE, sortedFiles.length)}` : "0"}
          {` of ${sortedFiles.length} files`}
        </small>
        {pageCount > 1 && (
          <div className="d-flex align-items-center mb-2" aria-label={`${title} pagination`}>
            <button type="button" className="btn btn-outline-primary btn-sm"
              disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}>Previous</button>
            <span className="small mx-3">Page {currentPage} of {pageCount}</span>
            <button type="button" className="btn btn-outline-primary btn-sm"
              disabled={currentPage === pageCount} onClick={() => setPage(currentPage + 1)}>Next</button>
          </div>
        )}
      </div>
    </div>
  );
};

const getFollowupDate = (event) => {
  const details = event.extendedProps || {};
  return getUploadDate(event.start) || getUploadDate(details.date || details.followupDate);
};

const LatestFollowupSummary = ({ events, eventsError }) => {
  const latestEvent = [...events].sort((left, right) => {
    const leftDate = getFollowupDate(left);
    const rightDate = getFollowupDate(right);
    return (rightDate ? rightDate.getTime() : 0) - (leftDate ? leftDate.getTime() : 0);
  })[0];
  const details = latestEvent ? latestEvent.extendedProps || {} : {};
  const date = latestEvent ? getFollowupDate(latestEvent) : null;
  const fields = [
    ["Follow-Up Date", date ? date.toLocaleDateString("en-GB", {
      day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Asia/Bangkok",
    }) : ""],
    ["Follow-Up Type", details.type],
    ["Status", details.status],
    ["Doctor Next Step", details.doctorActionStatus],
    ["Doctor Consult", details.doctorConsult],
    ["Technician Consult", details.technicianConsult],
    ["Node", details.node],
    ["Order", details.order],
  ];

  return (
    <section className="border-bottom pb-2 mb-3" aria-label="Latest Follow-Up">
      <h5 className="mb-3">Latest Follow-Up</h5>
      {eventsError ? (
        <p className="text-muted">Unable to load follow-up information. Files are still available below.</p>
      ) : latestEvent ? (
        <dl className="row mb-0">
          {fields.map(([label, value]) => (
            <div className="col-sm-6 col-xl-3 mb-2" key={label} style={{ overflowWrap: "anywhere" }}>
              <dt className="small text-muted font-weight-normal">{label}</dt>
              <dd className="mb-0">{value && value !== "กรุณาเลือก" ? value : "-"}</dd>
            </div>
          ))}
        </dl>
      ) : <p className="text-muted">No follow-up event found</p>}
    </section>
  );
};

const PatientCpapFollowupFiles = ({ files, events = [], eventsError = false, onViewFile, isPreviewLoading = false }) => {
  const usageReports = Array.isArray(files.usageReports) ? files.usageReports : [];
  const questionnaires = Array.isArray(files.questionnaires) ? files.questionnaires : [];
  return (
    <div className="col-12 mb-4 d-flex">
      <div className="card border-0 pb-0 h-100 w-100">
        <div className="card-body">
          <h4 className="card-title mb-3">Follow-Up Report / Questionnaire</h4>
          <LatestFollowupSummary events={events} eventsError={eventsError} />
          {isPreviewLoading && <div className="small text-muted mb-2" role="status">Opening file...</div>}
          <Tabs id="patient-cpap-followup-files" defaultActiveKey="reports">
            <Tab eventKey="reports" title={`CPAP Usage Report (${usageReports.length})`}>
              <FollowupFileList title="CPAP Usage Report" files={usageReports}
                onViewFile={onViewFile} isPreviewLoading={isPreviewLoading} />
            </Tab>
            <Tab eventKey="questionnaires" title={`Questionnaire (${questionnaires.length})`}>
              <FollowupFileList title="Questionnaire" files={questionnaires}
                onViewFile={onViewFile} isPreviewLoading={isPreviewLoading} />
            </Tab>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default PatientCpapFollowupFiles;
